/**
 * PublicQuotePage — Public page for client to view, accept & sign a quote
 * Accessible via /devis/:token (no authentication required)
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, FileText, MapPin, Clock, Car, Users, Euro, AlertCircle, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import config from 'config';

const API_URL = config.API_URL;

const PublicQuotePage = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [result, setResult] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showPaymentChoice, setShowPaymentChoice] = useState(false);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/quote/${token}`);
        if (!res.ok) throw new Error('Devis non trouvé');
        const data = await res.json();
        setQuote(data);
        if (data.status === 'accepted') {
          setAccepted(true);
          setShowPaymentChoice(true);
        }
      } catch (err) {
        setError(err.message || 'Erreur');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchQuote();
  }, [token]);

  const handleAccept = async () => {
    if (!signerName.trim()) return;
    setAccepting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/quote/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signer_name: signerName.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setAccepted(true);
        setResult(data);
        setShowPaymentChoice(true);
      } else {
        // Already accepted — still show payment choice
        if (data.status === 'accepted') {
          setAccepted(true);
          setResult(data);
          setShowPaymentChoice(true);
        } else {
          setError(data.detail || 'Erreur');
        }
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setAccepting(false);
    }
  };

  const handlePay = async (paymentType) => {
    setPaymentLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/public/quote/${token}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_type: paymentType }),
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError(data.detail || 'Erreur lors de la création du paiement');
        setPaymentLoading(false);
      }
    } catch {
      setError('Erreur de connexion');
      setPaymentLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C9A24D]" />
      </div>
    );
  }

  if (error && !quote) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-4">
        <div className="bg-[#1F1F1F] rounded-2xl p-8 max-w-md w-full text-center border border-red-500/20">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[#E5E5E5] mb-2">Lien invalide</h1>
          <p className="text-[#E5E5E5]/60 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const company = quote?.company_info || {};

  return (
    <div className="min-h-screen bg-[#0B0B0B]" data-testid="public-quote-page">
      {/* Header */}
      <div className="bg-[#1F1F1F] border-b border-[#C9A24D]/20 py-6">
        <div className="max-w-2xl mx-auto px-4">
          <h1 className="text-2xl font-bold text-[#C9A24D] tracking-wide">Y GO</h1>
          <p className="text-[#E5E5E5]/50 text-xs tracking-widest">SHUTTLE SERVICES FRANCE</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Document Header */}
        <div className="bg-[#1F1F1F] rounded-2xl border border-[#C9A24D]/20 overflow-hidden mb-6">
          <div className="p-6 border-b border-[#C9A24D]/10">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#C9A24D]" />
                <h2 className="text-lg font-bold text-[#E5E5E5]">Devis {quote.document_number}</h2>
              </div>
              {accepted ? (
                <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium" data-testid="quote-accepted-badge">Accepté</span>
              ) : (
                <span className="px-3 py-1 rounded-full bg-[#C9A24D]/20 text-[#C9A24D] text-xs font-medium">En attente</span>
              )}
            </div>
            <p className="text-[#E5E5E5]/50 text-sm">Émis le {formatDate(quote.created_at)} — Valide {quote.validity_days} jours</p>
          </div>

          {/* Client info */}
          <div className="p-6 border-b border-white/5">
            <p className="text-[#E5E5E5]/40 text-xs uppercase tracking-wider mb-2">Client</p>
            <p className="text-[#E5E5E5] font-medium">{quote.client_name}</p>
            {quote.client_email && <p className="text-[#E5E5E5]/60 text-sm">{quote.client_email}</p>}
          </div>

          {/* Mission details */}
          <div className="p-6 space-y-4">
            <p className="text-[#E5E5E5]/40 text-xs uppercase tracking-wider">Détails de la prestation</p>

            <div className="grid grid-cols-1 gap-3">
              {quote.booking_ref && (
                <div className="flex items-start gap-3">
                  <FileText className="w-4 h-4 text-[#C9A24D] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[#E5E5E5]/50 text-xs">Référence</p>
                    <p className="text-[#C9A24D] font-mono font-medium">{quote.booking_ref}</p>
                  </div>
                </div>
              )}
              {quote.pickup_datetime && (
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-[#C9A24D] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[#E5E5E5]/50 text-xs">Date & Heure</p>
                    <p className="text-[#E5E5E5]">{formatDate(quote.pickup_datetime)}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[#E5E5E5]/50 text-xs">Départ</p>
                  <p className="text-[#E5E5E5]">{quote.pickup_address || '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[#E5E5E5]/50 text-xs">Destination</p>
                  <p className="text-[#E5E5E5]">{quote.dropoff_address || '-'}</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="flex items-start gap-3">
                  <Car className="w-4 h-4 text-[#C9A24D] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[#E5E5E5]/50 text-xs">Véhicule</p>
                    <p className="text-[#E5E5E5]">{quote.vehicle_type || 'Berline'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="w-4 h-4 text-[#C9A24D] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[#E5E5E5]/50 text-xs">Passagers</p>
                    <p className="text-[#E5E5E5]">{quote.passenger_count || 1}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Amount */}
          <div className="mx-6 mb-6 p-4 bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Euro className="w-5 h-5 text-[#C9A24D]" />
                <span className="text-[#E5E5E5] font-medium">Montant TTC</span>
              </div>
              <span className="text-2xl font-bold text-[#C9A24D]" data-testid="quote-amount">
                {typeof quote.amount === 'number' ? quote.amount.toFixed(2) : quote.amount} €
              </span>
            </div>
          </div>

          {quote.notes && (
            <div className="mx-6 mb-6 p-3 bg-[#0B0B0B] rounded-lg">
              <p className="text-[#E5E5E5]/50 text-xs mb-1">Notes</p>
              <p className="text-[#E5E5E5]/80 text-sm">{quote.notes}</p>
            </div>
          )}
        </div>

        {/* CGV */}
        {quote.cgv_text && (
          <div className="bg-[#1F1F1F] rounded-2xl border border-[#333] p-6 mb-6">
            <h3 className="text-[#E5E5E5] font-semibold text-sm mb-3">Conditions Générales de Vente</h3>
            <p className="text-[#E5E5E5]/50 text-xs leading-relaxed">{quote.cgv_text}</p>
          </div>
        )}

        {/* Acceptance / Payment Section */}
        {accepted ? (
          showPaymentChoice ? (
            <div className="bg-[#1F1F1F] rounded-2xl border border-[#C9A24D]/30 p-6" data-testid="quote-payment-choice">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                <div>
                  <h3 className="text-lg font-bold text-emerald-400">Devis accepté</h3>
                  <p className="text-[#E5E5E5]/50 text-xs">
                    {result?.transport_order_number && `Bon de transport : ${result.transport_order_number}`}
                  </p>
                </div>
              </div>

              <div className="border-t border-[#C9A24D]/10 pt-4 mt-2">
                <h4 className="text-[#E5E5E5] font-semibold mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#C9A24D]" />
                  Choisissez votre mode de paiement
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button onClick={() => handlePay('deposit')} disabled={paymentLoading}
                    className="p-4 bg-[#0B0B0B] border-2 border-[#C9A24D]/30 rounded-xl hover:border-[#C9A24D] transition-colors text-left"
                    data-testid="pay-deposit-btn">
                    <div className="text-[#C9A24D] font-bold text-lg mb-1">Acompte 30%</div>
                    <div className="text-[#E5E5E5] text-xl font-bold">
                      {quote?.amount ? (quote.amount * 0.3).toFixed(2) : '0.00'} €
                    </div>
                    <p className="text-[#E5E5E5]/40 text-xs mt-1">Réservez maintenant, payez le solde plus tard</p>
                  </button>
                  <button onClick={() => handlePay('full')} disabled={paymentLoading}
                    className="p-4 bg-[#0B0B0B] border-2 border-emerald-500/30 rounded-xl hover:border-emerald-400 transition-colors text-left"
                    data-testid="pay-full-btn">
                    <div className="text-emerald-400 font-bold text-lg mb-1">Totalité</div>
                    <div className="text-[#E5E5E5] text-xl font-bold">
                      {quote?.amount ? quote.amount.toFixed(2) : '0.00'} €
                    </div>
                    <p className="text-[#E5E5E5]/40 text-xs mt-1">Réglez la totalité et confirmez votre réservation</p>
                  </button>
                </div>
                {paymentLoading && (
                  <div className="flex items-center justify-center gap-2 mt-4 text-[#C9A24D]">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Redirection vers Stripe...</span>
                  </div>
                )}
                {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
              </div>
            </div>
          ) : (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6 text-center" data-testid="quote-accepted-section">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-emerald-400 mb-2">Devis accepté</h3>
              <p className="text-[#E5E5E5]/60 text-sm">Merci pour votre confiance.</p>
            </div>
          )
        ) : (
          <div className="bg-[#1F1F1F] rounded-2xl border border-[#C9A24D]/30 p-6" data-testid="quote-accept-section">
            <h3 className="text-lg font-bold text-[#E5E5E5] mb-2">Accepter ce devis</h3>
            <p className="text-[#E5E5E5]/50 text-sm mb-4">
              En acceptant ce devis, vous reconnaissez avoir pris connaissance des conditions générales de vente et validez la prestation décrite ci-dessus.
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-[#E5E5E5]/70 text-sm block mb-1">Votre nom complet *</label>
                <Input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Prénom et Nom"
                  className="bg-[#0B0B0B] border-[#C9A24D]/30 text-[#E5E5E5]"
                  data-testid="signer-name-input"
                />
              </div>
              <Button
                onClick={handleAccept}
                disabled={accepting || !signerName.trim()}
                className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] py-3 text-base font-semibold"
                data-testid="accept-quote-btn"
              >
                {accepting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                Accepter & Signer
              </Button>
            </div>
            {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-[#E5E5E5]/30 text-xs">
            {company.legal_name || 'Y GO SHUTTLE SERVICES FRANCE'} — {company.address} {company.postal_code} {company.city}
          </p>
          <p className="text-[#E5E5E5]/20 text-xs mt-1">
            SIRET: {company.siret} | TVA: {company.tva_number}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicQuotePage;
